// @ts-strict-ignore
import React, { useEffect, useRef, useReducer, useState } from 'react'
import Flickity, { FlickityOptions } from 'react-flickity-component'
import { isRTL } from 'mobile_web/modules/platform'

import 'flickity/css/flickity.css'
import usePrevious from 'common_lib/hooks/use-previous'

interface FlickityProps {
	className?: string
	disableImagesLoaded?: boolean
	elementType?: string
	flickityRef?: (ref: Flickity) => void
	options?: FlickityOptions
	reloadOnUpdate?: boolean
	static?: boolean
}

interface Props {
	update?: boolean
	renderItems: () => JSX.Element | JSX.Element[]
	sliderOptions?: FlickityProps
	activeSlideIndex?: number
	onChange?: (selectedIndex: number) => void
	onStaticClick?: (selectedIndex: number) => void
	flkty: (flkty: Flickity) => void
}

export const FlickitySlider: React.FC<Props> = (props): JSX.Element => {
	const flkty: React.MutableRefObject<Flickity> = useRef(null)
	const [, forceUpdate] = useReducer(x => x + 1, 0)
	const [updated, setUpdated] = useState<boolean>(false)

	const newOptions = props.sliderOptions?.options || {}
	const options = {
		pageDots: false,
		prevNextButtons: false,
		rightToLeft: isRTL(),
		...newOptions,
	}

	const prevActiveSlideIndex = usePrevious<number>(props.activeSlideIndex) || 0

	const onChange = () => {
		if (prevActiveSlideIndex !== flkty?.current?.selectedIndex) {
			props?.onChange(flkty?.current?.selectedIndex)
		}
	}

	const onStaticClick = (_event, _pointer, _cellElement, cellIndex) => {
		if (cellIndex !== flkty?.current?.selectedIndex) {
			flkty?.current?.select(cellIndex)
		}
		props.onStaticClick?.(cellIndex)
	}

	useEffect(() => {
		flkty?.current?.on('change', onChange)
		flkty?.current?.on('staticClick', onStaticClick)

		return () => {
			flkty?.current?.off('change', onChange)
			flkty?.current?.off('staticClick', onStaticClick)
		}
	}, [flkty?.current, prevActiveSlideIndex])

	useEffect(() => {
		if (!updated && (flkty?.current as any)?.slidesWidth === 0) {
			flkty?.current?.resize()
			setTimeout(() => forceUpdate(), 0)
			setUpdated(true)
		}
	})

	const onSwiperRef = (ref: Flickity) => {
		if ((ref as any)?.slidesWidth === 0) {
			setUpdated(false)
			setTimeout(() => forceUpdate(), 1000)
		}

		flkty.current = ref

		props.flkty(flkty.current)
	}

	return (
		<Flickity {...props.sliderOptions} flickityRef={onSwiperRef} options={options} reloadOnUpdate={false}>
			{props.renderItems()}
		</Flickity>
	)
}
